/**
 * NavigationControls Component
 * Handles Next, Back, and Submit buttons for form navigation
 */

import AccessibleButton from '../AccessibleButton'

interface NavigationControlsProps {
  onNext?: () => void
  onBack?: () => void
  onSubmit?: () => void
  showBack: boolean
  showNext: boolean
  showSubmit: boolean
  isNextDisabled?: boolean
  isSubmitting?: boolean
  nextLabel?: string
  backLabel?: string
  submitLabel?: string
  className?: string
}

const NavigationControls = ({
  onNext,
  onBack,
  onSubmit,
  showBack,
  showNext,
  showSubmit,
  isNextDisabled = false,
  isSubmitting = false,
  nextLabel = 'Next',
  backLabel = 'Back',
  submitLabel = 'Submit',
  className = ''
}: NavigationControlsProps) => {
  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      {/* Back Button */}
      <div className="flex-shrink-0">
        {showBack && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:text-gray-900 transition-colors"
            aria-label="Go to previous question"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="font-medium">{backLabel}</span>
          </button>
        )}
      </div>

      {/* Next/Submit Button */}
      <div className="flex-grow flex justify-end">
        {showNext && onNext && (
          <AccessibleButton
            type="button"
            onClick={onNext}
            disabled={isNextDisabled}
            variant="primary"
            className="min-w-[120px]"
          >
            <span className="flex items-center justify-center gap-2">
              {nextLabel}
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </span>
          </AccessibleButton>
        )}

        {showSubmit && onSubmit && (
          <AccessibleButton
            type="button"
            onClick={onSubmit}
            disabled={isSubmitting}
            variant="primary"
            className="min-w-[160px]"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Submitting...
              </span>
            ) : (
              submitLabel
            )}
          </AccessibleButton>
        )}
      </div>
    </div>
  )
}

export default NavigationControls
